import { getLocalizedStringValue } from '@griply/utils';
import {
  useLocalizedLifeAreaById,
  useUpdateLifeArea,
} from 'features/life-areas';
import { useVisionByLifeAreaId } from 'features/vision';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router';
import {
  EditableTextField,
  Variants,
} from 'shared/components/ui/editable-text-field';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { Paths } from 'shared/routes/paths';

import { LifeAreaDescription } from './life-area-description';
import { LifeAreaIllustration } from './life-area-illustration';
import * as Styled from './life-area-summary-header.style';

export const LifeAreaSummaryHeader: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const locale = useI18nLocale();
  const lifeArea = useLocalizedLifeAreaById(id);
  const { submit: updateLifeArea } = useUpdateLifeArea();
  const vision = useVisionByLifeAreaId(id);
  const { t } = useTranslation();

  const onDescriptionClick = useCallback(
    () => id && navigate(generatePath(Paths.LifeAreaDetailVision, { id })),
    [id, navigate],
  );

  if (!lifeArea) {
    return null;
  }

  const onChangeTitle = (title: string) =>
    !lifeArea.archivedAt &&
    !lifeArea.frozenAt &&
    updateLifeArea({ ...lifeArea, name: title });

  return (
    <PageSectionBase>
      <Styled.Header>
        <Styled.Container>
          <Styled.IllustrationContainer
            $hasImage={!!lifeArea.visionPreview?.image}
          >
            <Styled.ImageContainer $hasImage={!!lifeArea.visionPreview?.image}>
              <LifeAreaIllustration
                illustration={
                  lifeArea.visionPreview?.image ?? lifeArea.iconName
                }
              />
            </Styled.ImageContainer>
          </Styled.IllustrationContainer>

          <Styled.Body>
            <Styled.Content>
              <Styled.ContentHeader>
                <EditableTextField
                  value={getLocalizedStringValue(lifeArea.name, locale)}
                  onChange={onChangeTitle}
                  variant={Variants.H4}
                  isStrikeThrough={!!lifeArea.archivedAt}
                />

                <Styled.DescriptionContainer>
                  <LifeAreaDescription
                    value={vision?.description}
                    onClick={onDescriptionClick}
                    disabled={!!lifeArea.archivedAt || !!lifeArea.frozenAt}
                    placeholder={t(
                      'pages.life-area-detail.vision.text-area.placeholder',
                    )}
                  />
                </Styled.DescriptionContainer>
              </Styled.ContentHeader>
            </Styled.Content>
          </Styled.Body>
        </Styled.Container>
      </Styled.Header>
    </PageSectionBase>
  );
};
